export interface QuestionField {
  id: string;
  label: string;
  type: 'select' | 'text' | 'date';
  options?: { value: string; label: string }[];
  required?: boolean;
  placeholder?: string;
  defaultValue?: string;
}

export interface ContextQuestion {
  id: string;
  fields: QuestionField[];
  hidden_data?: Record<string, string>;
}

const stripDanglingCommentTerminators = (value: string) =>
  value.replaceAll('--!>', '').replaceAll('-->', '');

export function parseContextQuestion(content: string): { cleanContent: string; question: ContextQuestion | null } {
  if (!content) return { cleanContent: content, question: null };

  let question: ContextQuestion | null = null;
  let cleanContent = content;

  const markerPatterns = [
    /<!--\s*CONTEXT_QUESTION\s*:\s*([\s\S]*?)(?:--!>|-->)/gi,
    /<!--CONTEXT_QUESTION:([\s\S]*?)(?:--!>|-->)/gi,
  ];

  for (const pattern of markerPatterns) {
    const matches = cleanContent.match(pattern);
    if (matches && matches.length > 0) {
      for (const fullMatch of matches) {
        const jsonMatch = fullMatch.match(/CONTEXT_QUESTION\s*:\s*([\s\S]*?)(?:--!>|-->)/i);
        if (jsonMatch && jsonMatch[1]) {
          const jsonStr = jsonMatch[1].trim();

          cleanContent = cleanContent.replace(fullMatch, '').trim();

          try {
            const normalizedJson = jsonStr
              .replace(/[\n\r\t]+/g, '')
              .replace(/\s{2,}/g, ' ')
              .replace(/,\s*([}\]])/g, '$1')
              .trim();

            const parsed = JSON.parse(normalizedJson);
            if (parsed.fields && Array.isArray(parsed.fields)) {
              question = {
                id: parsed.id || crypto.randomUUID(),
                fields: parsed.fields,
                hidden_data: parsed.hidden_data || undefined,
              };
              break;
            }
          } catch (e) {
            console.error('Failed to parse CONTEXT_QUESTION JSON:', e);
          }
        }
      }
      if (question) break;
    }
  }

  if (!question) {
    const rawJsonRegex = /\{"id"\s*:\s*"[^"]*"[^{}]*"fields"\s*:\s*\[[\s\S]*?\]\s*(?:,"hidden_data"\s*:\s*\{[^}]*\}\s*)?\}/g;
    const rawMatches = cleanContent.match(rawJsonRegex);

    if (rawMatches) {
      for (const rawJson of rawMatches) {
        try {
          const normalized = rawJson.replace(/[\n\r\t]+/g, '').replace(/\s{2,}/g, ' ').trim();
          const parsed = JSON.parse(normalized);

          if (parsed.fields && Array.isArray(parsed.fields)) {
            cleanContent = cleanContent.replace(rawJson, '').trim();
            question = {
              id: parsed.id || crypto.randomUUID(),
              fields: parsed.fields,
              hidden_data: parsed.hidden_data || undefined,
            };
            break;
          }
        } catch {
          // Continue to next match
        }
      }
    }
  }

  cleanContent = cleanContent
    .replace(/<!--\s*CONTEXT_QUESTION\s*:[\s\S]*?(?:--!>|-->)/gi, '')
    .replace(/<!--CONTEXT_QUESTION:[\s\S]*?(?:--!>|-->)/gi, '')
    .replace(/<!--\s*CONTEXT_QUESTION[^>]*>?/gi, '')
    .replace(/CONTEXT_QUESTION\s*:\s*\{[\s\S]*?\}\s*(?:--!>|-->)/gi, '');

  cleanContent = stripDanglingCommentTerminators(cleanContent)
    .replace(/!\[[^\]]*\]\[data:image\/[^\]]+\]/gi, '')
    .replace(/!\[[^\]]*\]\(data:image\/[^)]+\)/gi, '')
    .replace(/%3Csvg%20xmlns[^)\s]+/gi, '')
    .replace(/data:image\/svg\+xml[;,][^\s)]+/gi, '')
    .replace(/\{\s*"?marketplace_listing_id"?\s*:[^}]+\}/gi, '')
    .replace(/\{\s*"?criticality_tier"?\s*:[^}]+\}/gi, '')
    .replace(/\{"id"\s*:\s*"[^"]*"[^}]*"fields"\s*:\s*\[[\s\S]*?\]\}/gi, '')
    .replace(/(?:^|\s)(?:Marketplace Listing Id|Criticality Tier|Due Date):\s*[a-f0-9-]+(?:\s|$)/gi, '')
    .replace(/(?:^|\n)\s*[a-f0-9]{8}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{12}\s*(?:\n|$)/gi, '\n')
    .replace(/\n{3,}/g, '\n\n')
    .trim();

  return { cleanContent, question };
}
